import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import PhoneInputModule from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { InputWrapper } from '~/@/components/inputs/base/input-wrapper'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { cjsDefault } from '~/common/utils/cjs-default'
import { RolesDropdown } from '../roles/RolesDropdown'
import { Button } from '../ui/atoms/button/Button'
import { Modal } from '../ui/molecules/modal/Modal'
import { Role } from './types'

const PhoneInput = cjsDefault(PhoneInputModule)

interface AddUserDialogProps {
  onClose: () => void
  onSubmit: (data: UserFormData) => Promise<void>
  roles: Role[]
}

export interface UserFormData {
  firstName: string
  lastName: string
  email?: string
  phoneNumber?: string
  roleIds: string[]
}

export function AddUserDialog({ onClose, onSubmit, roles }: AddUserDialogProps) {
  const { t } = useTypedTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<UserFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      roleIds: [],
    },
    mode: 'onTouched',
  })

  const onFormSubmit = handleSubmit(async (data) => {
    if (isSubmitting) {
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(data)
    } finally {
      setIsSubmitting(false)
    }
  })

  return (
    <Modal
      actions={
        <Button
          disabled={!isValid || isSubmitting}
          onClick={onFormSubmit}
          size="small"
          variant="secondary"
        >
          {t('user.add_user_dialog.send_invite')}
        </Button>
      }
      onClose={onClose}
      open
      showCancelButton
      title={t('user.add_user_dialog.title')}
    >
      <div>
        <div className="py-4">
          <form className="flex flex-col" onSubmit={onFormSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <Controller
                control={control}
                name="firstName"
                render={({ field }) => (
                  <div className="flex flex-col gap-1">
                    <ShortTextInput
                      {...field}
                      errorText={errors.firstName?.message}
                      id="firstName"
                      label={t('user.add_user_dialog.first_name')}
                    />
                  </div>
                )}
                rules={{ required: t('user.add_user_dialog.first_name_required') }}
              />

              <Controller
                control={control}
                name="lastName"
                render={({ field }) => (
                  <div className="flex flex-col gap-1">
                    <ShortTextInput
                      {...field}
                      errorText={errors.lastName?.message}
                      id="lastName"
                      label={t('user.add_user_dialog.last_name')}
                    />
                  </div>
                )}
                rules={{ required: t('user.add_user_dialog.last_name_required') }}
              />
            </div>

            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <div className="flex flex-col gap-1">
                  <ShortTextInput
                    {...field}
                    errorText={errors.email?.message}
                    id="email"
                    label={t('user.add_user_dialog.email')}
                  />
                </div>
              )}
              rules={{
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('user.add_user_dialog.invalid_email'),
                },
              }}
            />

            <Controller
              control={control}
              name="phoneNumber"
              render={({ field }) => (
                <InputWrapper label={t('user.add_user_dialog.phone_number')}>
                  {/* Always show the phone number input in LTR */}
                  <div dir="ltr">
                    <PhoneInput
                      country={'us'}
                      inputStyle={{ width: '100%' }}
                      onChange={field.onChange}
                      preferredCountries={['us', 'il', 'ca', 'de']}
                      value={field.value}
                    />
                  </div>
                </InputWrapper>
              )}
            />

            <Controller
              control={control}
              name="roleIds"
              render={({ field }) => (
                <RolesDropdown
                  {...field}
                  errorText={errors.roleIds?.message}
                  label={t('user.add_user_dialog.roles')}
                  required
                  roles={roles}
                  selectedRoleIds={field.value}
                />
              )}
              rules={{
                required: t('user.add_user_dialog.at_least_one_role'),
                validate: (value) => value.length > 0 || t('user.add_user_dialog.select_role'),
              }}
            />
          </form>
        </div>
      </div>
    </Modal>
  )
}
